import { Loader2 } from 'lucide-react';
import type { LifecycleState } from '../../types/lifecycle';

interface LifecycleBadgeProps {
	state: LifecycleState | undefined;
	/** Total badge size in px (default 10). Spinner scales with this. */
	size?: number;
}

const COLORS: Partial<Record<LifecycleState, string>> = {
	NEEDS_CONFIG: 'var(--khal-status-warning)',
	DEPLOYING: 'var(--khal-accent-blue)',
	DEGRADED: 'var(--khal-status-error)',
};

const LABELS: Record<LifecycleState, string> = {
	INSTALLED: 'Installed',
	NEEDS_CONFIG: 'Needs configuration',
	DEPLOYING: 'Deploying',
	RUNNING: 'Running',
	DEGRADED: 'Degraded',
	UNINSTALLING: 'Uninstalling',
};

/**
 * Small status indicator overlaid on app icons. Visual contract per WISH:
 *   INSTALLED   → no badge (steady ambient state)
 *   NEEDS_CONFIG → yellow dot
 *   DEPLOYING   → blue spinner
 *   RUNNING     → no badge
 *   DEGRADED    → red dot
 *   UNINSTALLING → gray dot (caller is expected to fade the icon itself)
 */
export function LifecycleBadge({ state, size = 10 }: LifecycleBadgeProps) {
	if (!state || state === 'INSTALLED' || state === 'RUNNING') return null;

	const label = LABELS[state];
	const baseStyle: React.CSSProperties = {
		width: size,
		height: size,
		borderRadius: '50%',
		display: 'inline-block',
		boxShadow: '0 0 0 2px var(--khal-surface-default)',
	};

	if (state === 'DEPLOYING') {
		return (
			<span
				role="status"
				aria-label={label}
				data-testid={`lifecycle-badge-${state}`}
				style={{
					...baseStyle,
					background: 'transparent',
					color: COLORS.DEPLOYING,
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Loader2 size={size} className="animate-spin" aria-hidden="true" />
			</span>
		);
	}

	if (state === 'UNINSTALLING') {
		return (
			<span
				role="status"
				aria-label={label}
				data-testid={`lifecycle-badge-${state}`}
				style={{ ...baseStyle, background: 'var(--khal-text-muted)', opacity: 0.6 }}
			/>
		);
	}

	return (
		<span
			role="status"
			aria-label={label}
			data-testid={`lifecycle-badge-${state}`}
			style={{ ...baseStyle, background: COLORS[state] }}
		/>
	);
}
